import * as React from 'react';
import classnames from '../utils/classnames.tsx';
import { useControllableState } from '../utils/useControllableState.tsx';
import { Slot } from '@radix-ui/react-slot';

interface BaseButtonProps extends React.ComponentPropsWithoutRef<'button'> {
    loading?: boolean;
    asChild?: boolean;
}

export const BaseButton = ({ className, loading, asChild, onClick, disabled, ...props }: BaseButtonProps) => {
    const [isLoading, setIsLoading] = useControllableState(false, loading);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        setIsLoading(true);
        Promise.resolve(onClick?.(e)).finally(() => setIsLoading(false));
    };

    const Comp = asChild ? Slot : 'button';
    return (
        <Comp
            className={classnames((disabled || isLoading) && 'opacity-50 cursor-not-allowed', className)}
            onClick={handleClick}
            disabled={disabled || isLoading}
            {...props}
        />
    );
};

const Button = ({
    className,
    active,
    variant = 'outline',
    ...props
}: BaseButtonProps & {
    active?: boolean;
    variant?: 'outline' | 'ghost' | 'primary';
}) => {
    return (
        <BaseButton
            className={classnames(
                'inline-block bg-surface-weak text-content-strong rounded px-3 py-1.5 font-medium text-sm border border-solid outline-none not-disabled:cursor-pointer',
                'transition-all',
                'data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong',
                variant === 'outline' &&
                    'bg-surface-weak border-line-strong shadow-control data-[state=open]:border-line-brand-default hover:not-disabled:bg-surface-strong hover:not-disabled:text-content-strong',
                active && variant === 'outline' && '!border-line-brand-default',
                variant === 'ghost' &&
                    'bg-surface-transparent border-line-transparent hover:not-disabled:bg-surface-strong hover:not-disabled:text-content-strong',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:not-disabled:!bg-surface-brand-medium-hover active:not-disabled:!bg-surface-brand-medium-active',
                variant === 'primary' &&
                    'bg-surface-brand-strong-default text-content-white dark:text-content-black hover:not-disabled:!bg-surface-brand-strong-hover border-line-transparent focus:not-disabled:!bg-surface-brand-strong-focus active:not-disabled:!bg-surface-brand-strong-active disabled:opacity-50',
                className
            )}
            {...props}
        />
    );
};

export default Button;
